import React from 'react';
import { Button, Card, Input, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { hrmsStatusColor } from '../shared/hrmsStatus';
import { useRecruitList } from './shared/useRecruitList';
import RecruitPeoplePicker from './shared/RecruitPeoplePicker';
import { exportRows, fmtDate, stageLabel } from './shared/recruitUi';

const { Text } = Typography;
const STATUS_OPTIONS = ['Draft', 'PendingApproval', 'Open', 'OnHold', 'Filled', 'Rejected', 'Cancelled'];
const FILTERS = ['status', 'department_id', 'recruiter_employee_id'] as const;

const RequisitionsPage: React.FC = () => {
  const navigate = useNavigate();
  const list = useRecruitList(FILTERS);
  const { data: departments = [] } = useDepartmentsForSelection();

  const { data, isFetching } = useQuery({
    queryKey: hrmsKeys.requisitions(list.apiParams),
    queryFn: () => hrmsService.pageRequisitions(list.apiParams),
    placeholderData: keepPreviousData,
  });
  const rows = data?.rows || [];

  const exportAll = async () => {
    const all = await hrmsService.pageRequisitions({ ...list.apiParams, page: 1, limit: 5000 });
    await exportRows(
      'manpower-requisitions',
      [
        ['requisition_number', 'Requisition #'],
        ['job_title', 'Title'],
        ['department_name', 'Department'],
        ['headcount', 'Headcount'],
        ['filled_count', 'Filled'],
        ['urgency', 'Urgency'],
        ['recruiter_name', 'Recruiter'],
        ['hiring_manager_name', 'Hiring manager'],
        ['target_join_date', 'Target join'],
        ['age_days', 'Age (days)'],
        ['status', 'Status'],
      ],
      all.rows
    );
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Requisition #',
      dataIndex: 'requisition_number',
      key: 'requisition_number',
      width: 140,
      fixed: 'left',
      sorter: true,
      sortOrder: list.sortOrderFor('requisition_number'),
      render: (v: string, r) => <a onClick={() => navigate(`/hr/requisitions/${r.id}`)}>{v}</a>,
    },
    { title: 'Title', dataIndex: 'job_title', key: 'job_title', ellipsis: true, sorter: true, sortOrder: list.sortOrderFor('job_title') },
    { title: 'Department', dataIndex: 'department_name', width: 160, ellipsis: true },
    {
      title: 'Filled',
      key: 'fill',
      width: 90,
      render: (_, r) => `${r.filled_count ?? 0} / ${r.headcount}`,
    },
    { title: 'Urgency', dataIndex: 'urgency', width: 95 },
    { title: 'Recruiter', dataIndex: 'recruiter_name', width: 150, ellipsis: true, render: (v) => v || <Text type="secondary">Unassigned</Text> },
    {
      title: 'Target join',
      dataIndex: 'target_join_date',
      key: 'target_join_date',
      width: 120,
      sorter: true,
      sortOrder: list.sortOrderFor('target_join_date'),
      render: (v) => fmtDate(v),
    },
    {
      title: 'Age',
      dataIndex: 'age_days',
      width: 80,
      render: (v: number, r) =>
        ['Open', 'PendingApproval', 'OnHold'].includes(r.status) ? (
          <Text type={v > 60 ? 'danger' : v > 30 ? 'warning' : undefined}>{v}d</Text>
        ) : (
          '—'
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (s: string) => <Tag color={hrmsStatusColor(s)}>{stageLabel(s)}</Tag>,
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Manpower requisitions"
          subtitle="Approved headcount requests — the starting point for every hire"
          primaryActions={
            <Space>
              <Button icon={<DownloadOutlined />} onClick={exportAll}>
                Export
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/requisitions/new')}>
                New requisition
              </Button>
            </Space>
          }
        />
      }
    >
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Requisition # or title"
            defaultValue={list.q}
            onSearch={list.setQ}
            style={{ width: 240 }}
          />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 170 }}
            value={list.filters.status}
            onChange={(v) => list.setFilter('status', v)}
            options={STATUS_OPTIONS.map((v) => ({ value: v, label: stageLabel(v) }))}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Department"
            style={{ width: 200 }}
            value={list.filters.department_id}
            onChange={(v) => list.setFilter('department_id', v)}
            options={departments.map((d: any) => ({ value: d.id, label: d.department_name || d.name }))}
          />
          <RecruitPeoplePicker
            placeholder="Recruiter"
            style={{ width: 200 }}
            value={list.filters.recruiter_employee_id}
            onChange={(v) => list.setFilter('recruiter_employee_id', v as string)}
          />
          {list.activeFilterCount > 0 && (
            <Button type="link" onClick={list.reset}>
              Clear filters
            </Button>
          )}
        </Space>
      </Card>
      <Table
        rowKey="id"
        loading={isFetching}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 1200 }}
        pagination={list.pagination(data?.total || 0)}
        onChange={list.onTableChange}
        onRow={(r) => ({ onDoubleClick: () => navigate(`/hr/requisitions/${r.id}`) })}
      />
    </WorkspaceLayout>
  );
};

export default RequisitionsPage;
